import { type ApiError, apiErrorSchema } from "@procuremate/shared";

/** Thrown for any non-2xx response. `code` is a stable ERROR_CODES value. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

type Json = Record<string, unknown> | unknown[];

/**
 * Same-origin fetch wrapper (ADR-0011): session cookie is sent automatically,
 * no tokens in JS. Always use this instead of calling fetch directly.
 */
export async function api<T>(
  path: string,
  /** `form`: a multipart upload (the browser sets its Content-Type with the boundary). */
  init: { method?: string; body?: Json; form?: FormData; signal?: AbortSignal } = {},
): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: init.method ?? (init.body || init.form ? "POST" : "GET"),
    credentials: "same-origin",
    headers: init.body
      ? { "Content-Type": "application/json", Accept: "application/json" }
      : { Accept: "application/json" },
    body: init.form ?? (init.body ? JSON.stringify(init.body) : undefined),
    signal: init.signal,
  });

  if (response.status === 204) return undefined as T;
  if (!response.ok) throw await errorFromResponse(response);
  return (await response.json().catch(() => undefined)) as T;
}

/** The typed error for a non-2xx response (reads the standard `{ error }` body when present). */
export async function errorFromResponse(response: Response): Promise<ApiRequestError> {
  const payload: unknown = await response.json().catch(() => undefined);
  const parsed = apiErrorSchema.safeParse(payload);
  const error: ApiError["error"] = parsed.success
    ? parsed.data.error
    : { code: "INTERNAL", message: `Request failed (${response.status})` };
  return new ApiRequestError(response.status, error.code, error.message, error.details);
}
